/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description Helper function to setup message data
 */

import { Origin, type OriginWithoutEctaSuffix } from '@zcode/nr-utils'
import configureForBuilding from '@zcode/nr/es/shared/actions/sendMessage/helper/configureForBuilding'
import configureForListing from '@zcode/nr/es/shared/actions/sendMessage/helper/configureForListing'
import configureForMultiMsg from '@zcode/nr/es/shared/actions/sendMessage/helper/configureForMultiMsg'
import configureMsg from '@zcode/nr/es/shared/actions/sendMessage/helper/configureMsg'
import getMessagedListingAttrs from '@zcode/nr/es/shared/actions/sendMessage/helper/getMessagedListingAttrs'
import {
  APPLY,
  MESSAGE,
  MESSAGE_FORM_INLINE,
  MESSAGE_FORM_MODAL,
  ONE_TAP_MESSAGE,
  TOUR,
} from '@zcode/nr/es/shared/constants/message'
import type { MessageData, SendMessageArgs } from '@zcode/state-type'
import type { O } from 'ts-toolbelt'

const doesntHaveEctaSuffix = (
  originText: NonNullable<'' | MessageData['origin']>
): originText is OriginWithoutEctaSuffix | '' => {
  return (
    !originText.endsWith(Origin.ECTA_MESSAGE_SUFFIX) &&
    !originText.endsWith(Origin.ECTA_TOUR_SUFFIX) &&
    !originText.endsWith(Origin.ECTA_APPLY_SUFFIX)
  )
}

export const maybeAddEctaSuffix = <T extends NonNullable<'' | MessageData['origin']>>(
  originText: T,
  equalCTAIntent: SendMessageArgs['equalCTAIntent']
) => {
  if (doesntHaveEctaSuffix(originText)) {
    if (equalCTAIntent === MESSAGE) {
      return `${originText}${Origin.ECTA_MESSAGE_SUFFIX}` as const
    }
    if (equalCTAIntent === TOUR && !originText.endsWith('-tour')) {
      return `${originText}${Origin.ECTA_TOUR_SUFFIX}` as const
    }
    if (equalCTAIntent === APPLY) {
      return `${originText}${Origin.ECTA_APPLY_SUFFIX}` as const
    }
  }
  return originText
}

export type SetupMessageDataProps = O.Pick<
  SendMessageArgs,
  | 'equalCTAIntent'
  | 'entity'
  | 'formType'
  | 'isMulti'
  | 'messageData'
  | 'multiListables'
  | 'selectedFloorplanId'
> & {
  isMobile: boolean
}

export const setupMessageData = ({
  equalCTAIntent,
  entity,
  formType,
  isMobile,
  isMulti,
  messageData,
  multiListables,
  selectedFloorplanId,
}: SetupMessageDataProps) => {
  // extract: listingIds, buildingIds, messagedListingIds, messagedBuildingIds, originalTarget info
  if (isMulti) {
    configureForMultiMsg(messageData, entity, multiListables)
  } else if (entity.isBuilding) {
    configureForBuilding(messageData, entity, selectedFloorplanId)
  } else {
    configureForListing(messageData, entity)
  }
  // set default message if messageData does not contain a message
  if (!messageData.msg) {
    messageData.msg = configureMsg(entity)
    messageData.messageModified = false
  }
  if (isMulti) {
    messageData.feedNames = getMessagedListingAttrs('feedName', multiListables)
    messageData.completionEvtName = isMobile ? 'MessageSimilarMobile' : 'MessageSimilar'
  }

  if (formType === MESSAGE_FORM_MODAL) {
    messageData.completionEvtName = 'Message Modal'
  } else if (formType === MESSAGE_FORM_INLINE) {
    messageData.completionEvtName = 'MessageInline'
  } else if (formType === ONE_TAP_MESSAGE) {
    messageData.completionEvtName = 'One Tap'
  }

  if (!messageData.ignoreEqualCTAOrigin) {
    messageData.origin = maybeAddEctaSuffix(messageData.origin, equalCTAIntent) || undefined
  }

  return messageData
}
